import { useMemo, useState } from "react";
import { ARTICLES, CATEGORIES } from "@/mocks/contents";
import type { FeedItem } from "@/mocks/contents";

function FeedCard({
  item,
  faved,
  onToggle,
}: {
  item: FeedItem;
  faved: boolean;
  onToggle: (id: string) => void;
}) {
  return (
    <article className="group flex flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <div className="relative block h-44 w-full overflow-hidden sm:h-48">
        <img
          src={item.img}
          alt={item.title}
          className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 rounded-full bg-foreground-950/60 px-2.5 py-1 text-xs font-semibold text-background-50 backdrop-blur">
          {item.tag}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-5">
        <a href="#gallery" className="cursor-pointer">
          <h3 className="line-clamp-2 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
            {item.title}
          </h3>
        </a>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{item.summary}</p>
        <div className="mt-4 flex items-center justify-between border-t border-background-100 pt-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-accent-500" aria-hidden="true"></span>
            {item.author}
          </span>
          <span>{item.time}</span>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span className="inline-flex items-center gap-1">
              <i className="ri-eye-line text-sm" aria-hidden="true"></i>
              {item.views}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-chat-3-line text-sm" aria-hidden="true"></i>
              {item.comments}
            </span>
          </div>
          <button
            type="button"
            aria-pressed={faved}
            onClick={() => onToggle(item.id)}
            className={`flex h-8 w-8 cursor-pointer items-center justify-center rounded-full transition-colors ${
              faved ? "bg-primary-100 text-primary-600" : "bg-background-100 text-foreground-500 hover:text-primary-600"
            }`}
            title={faved ? "取消收藏" : "收藏本文"}
          >
            <i className={`text-base ${faved ? "ri-heart-3-fill" : "ri-heart-3-line"}`} aria-hidden="true"></i>
          </button>
        </div>
      </div>
    </article>
  );
}

export default function FeedGrid() {
  const [active, setActive] = useState("all");
  const [favorites, setFavorites] = useState<Record<string, boolean>>({});
  const [showAll, setShowAll] = useState(false);

  const list = useMemo(
    () => (active === "all" ? ARTICLES : ARTICLES.filter((a) => a.cat === active)),
    [active],
  );
  const visible = showAll ? list : list.slice(0, 6);
  const favCount = Object.values(favorites).filter(Boolean).length;

  const toggleFav = (id: string) =>
    setFavorites((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <section id="gallery" className="w-full bg-background-100 py-12 md:py-16" aria-label="分类图文">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="text-xs font-bold tracking-[0.2em] text-primary-600">FEED · 分类图文</span>
            <h2 className="mt-2 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
              实锤图文 · 按频道追踪
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600">
              点按频道一键切换信息流，把同一主题的图文、现场与后续放在同一屏，追更不断层。
            </p>
          </div>
          <p className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground-500">
            <i className="ri-heart-3-fill text-primary-500" aria-hidden="true"></i>
            已收藏 {favCount} 篇
          </p>
        </div>

        <div className="mt-7 flex flex-wrap items-center gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => {
                setActive(c.id);
                setShowAll(false);
              }}
              className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                active === c.id
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-50 text-foreground-700 ring-1 ring-background-200 hover:text-primary-700"
              }`}
            >
              {c.name}
              {c.id === "all" ? `（${ARTICLES.length}）` : `（${ARTICLES.filter((a) => a.cat === c.id).length}）`}
            </button>
          ))}
        </div>

        <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((item) => (
            <FeedCard key={item.id} item={item} faved={!!favorites[item.id]} onToggle={toggleFav} />
          ))}
        </div>

        {list.length > 6 && (
          <div className="mt-9 flex justify-center">
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full bg-background-50 px-6 py-2.5 text-sm font-semibold text-foreground-800 ring-1 ring-background-300 transition-colors hover:bg-accent-500 hover:text-background-50 hover:ring-accent-500"
            >
              {showAll ? "收起内容" : `展开全部 ${list.length} 篇`}
              <i className={`text-base ${showAll ? "ri-arrow-up-line" : "ri-arrow-down-line"}`} aria-hidden="true"></i>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}